import React, { useState, useEffect } from 'react';
import type { RoundsResponse, RoundsRacersResponse } from '@/lib/collections/rounds';
import { CoinCharacter } from './CoinCharacter';

const COIN_COLORS = [
  '#ff3da8', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#fb7185',
];

const REVEAL_CAPTIONS = [
  '🎲 And the coins are...',
  '🚀 Tonight\'s contenders revealed!',
  '💎 The starting grid is set!',
  '🏎️ Get ready — race starting soon!',
];

interface RevealPanelProps {
  round: RoundsResponse;
  racers: RoundsRacersResponse[];
  secondsLeft: number;
}

export const RevealPanel: React.FC<RevealPanelProps> = ({
  round,
  racers,
  secondsLeft,
}) => {
  const [captionIdx, setCaptionIdx] = useState(0);
  const [visibleCoins, setVisibleCoins] = useState<number[]>([]);

  useEffect(() => {
    const t = setInterval(() => {
      setCaptionIdx((i) => (i + 1) % REVEAL_CAPTIONS.length);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  // Stagger reveal of coin cards
  useEffect(() => {
    if (racers.length === 0) return;
    setVisibleCoins([]);
    racers.forEach((_, i) => {
      setTimeout(() => {
        setVisibleCoins((prev) => [...prev, i]);
      }, i * 200 + 100);
    });
  }, [racers.length]);

  return (
    <div className="space-y-6">
      {/* Caption */}
      <div
        className="text-center text-lg font-semibold py-3 px-6 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(52,211,153,0.12))',
          border: '1px solid rgba(251,191,36,0.4)',
          color: '#fbbf24',
        }}
      >
        {REVEAL_CAPTIONS[captionIdx]}
      </div>

      {/* Race-starts-in countdown */}
      <div className="text-center">
        <div className="text-sm font-medium mb-1" style={{ color: '#a78bfa' }}>
          Race starts in
        </div>
        <div
          className="text-5xl font-bold tabular-nums"
          style={{
            color: secondsLeft <= 5 ? '#ff3da8' : '#34d399',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {secondsLeft}s
        </div>
      </div>

      {/* Coins revealed with stagger animation */}
      {racers.length > 0 ? (
        <div
          className="rounded-3xl overflow-hidden p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(52,211,153,0.08), rgba(26,10,46,0.9))',
            border: '2px solid rgba(52,211,153,0.3)',
          }}
        >
          <div className="text-center text-xs font-bold uppercase tracking-wider mb-5" style={{ color: '#34d399' }}>
            🏁 THIS ROUND'S RACERS 🏁
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {racers.map((racer, i) => {
              const color = COIN_COLORS[i % COIN_COLORS.length];
              const isVisible = visibleCoins.includes(i);
              return (
                <div
                  key={racer.coinId}
                  className="flex flex-col items-center gap-2"
                  style={{
                    transition: 'opacity 0.4s ease, transform 0.5s cubic-bezier(0.34,1.56,0.64,1)',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.6) translateY(20px)',
                  }}
                >
                  <CoinCharacter
                    imageUrl={racer.imageUrl}
                    symbol={racer.symbol}
                    performancePct="0"
                    color={color}
                    size={56}
                    isMyPick={false}
                    animate
                  />
                  <div
                    className="text-xs font-bold px-2.5 py-1 rounded-full"
                    style={{ background: `${color}22`, color, border: `1.5px solid ${color}44` }}
                  >
                    ${racer.symbol}
                  </div>
                  <div className="text-xs" style={{ color: '#7c6b9c' }}>
                    {racer.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className="text-center py-10 rounded-3xl"
          style={{
            background: 'rgba(26,10,46,0.8)',
            border: '2px dashed rgba(52,211,153,0.3)',
          }}
        >
          <div className="text-4xl mb-2 animate-spin" style={{ animationDuration: '2s' }}>🎰</div>
          <div style={{ color: '#34d399' }}>Selecting final racers...</div>
        </div>
      )}

      {/* Picking note */}
      <div
        className="text-center py-3 px-5 rounded-2xl text-sm"
        style={{
          background: 'rgba(167,139,250,0.08)',
          border: '1px solid rgba(167,139,250,0.2)',
          color: '#a78bfa',
        }}
      >
        🔒 Picking opens when the race starts — get ready!
      </div>
    </div>
  );
};
